import React, { useEffect, useState } from 'react'
// import MonthChart from './MonthChart'
// import YearChart from './YearChart'
// import WeekChart from './WeekChart'
// import DayChart from './DayChart'
import { CITY_KEY_MAP, DATE_DAILY, DATE_FORMAT_SERVER, DATE_MONTHLY, DATE_WEEKLY, DATE_YEARLY, unslugify } from '../../../../constants'
import { DateTime } from 'luxon'
import MultiLineChart from './MultiLineChart'
import MultiLineBarChart from './MultiLineBarChart'

const ChartContainer = ({
    appliedFilters,
    data,
    rawApiData,
    isMultiRange,
    getData,
}) => {
    const [chartDates, setChartDates] = useState([]);
    const [graphData, setGraphData] = useState({ series: [], categories: [] });
    const [activeSeriesKeys, setActiveSeriesKeys] = useState([]);

    // ============================================================
    // SINGLE RANGE MODE — existing logic (detailed day/month data)
    // ============================================================

    function mergeCityYearData(baseDates, apiData) {

        console.log("Merging data with baseDates:", baseDates);
        console.log("API Data:", apiData);
        const dateMap = new Map(
            baseDates.map(d => [d.name, d])
        );

        Object.entries(CITY_KEY_MAP).forEach(([cityName, keys]) => {
            const cityData = apiData[cityName];
            if (!cityData) return;

            // CURRENT YEAR
            cityData.current_year.forEach(({ period, count }) => {
                const date = DateTime.fromISO(period).toFormat("yyyy-MM-dd");
                if (dateMap.has(date)) {
                    dateMap.get(date)[keys.current] = count;
                }
            });
            console.log("After processing current_year for", cityData, cityName);
            // LAST YEAR
            cityData.last_year.forEach(({ period, count }) => {
                const date = DateTime.fromISO(period)
                    .plus({ years: 1 }) // align last year to current year timeline
                    .toFormat("yyyy-MM-dd");

                if (dateMap.has(date)) {
                    dateMap.get(date)[keys.last] = count;
                }
            });
        });

        return baseDates;
    }

    const getAllMonthsOfYear = (startDateStr) => {
        const start = DateTime.fromISO(startDateStr).startOf("year");
        const months = [];

        for (let i = 0; i < 12; i++) {
            const current = start.plus({ months: i });

            months.push({
                name: current.toFormat(DATE_FORMAT_SERVER),
                SPACEPARK_CURRENT_YEAR: 0,
                SPACEPARK_LAST_YEAR: 0,
                MELBOURNE_CURRENT_YEAR: 0,
                MELBOURNE_LAST_YEAR: 0,
                LOS_ANGELES_CURRENT_YEAR: 0,
                LOS_ANGELES_LAST_YEAR: 0
            });
        }

        console.log("getAllMonthsOfYear called with:", months);
        return months;
    };

    const getAllDatesOfMonth = (startDateStr) => {
        if (!startDateStr) return [];

        const start = DateTime.fromISO(startDateStr).startOf("month");
        const end = start.endOf("month");

        const dates = [];
        let current = start;

        while (current <= end) {
            dates.push({
                name: current.toFormat(DATE_FORMAT_SERVER),
                SPACEPARK_CURRENT_YEAR: 0,
                SPACEPARK_LAST_YEAR: 0,
                MELBOURNE_CURRENT_YEAR: 0,
                MELBOURNE_LAST_YEAR: 0,
                LOS_ANGELES_CURRENT_YEAR: 0,
                LOS_ANGELES_LAST_YEAR: 0
            });

            current = current.plus({ days: 1 });
        }

        return dates;
    };

    const getAllDaysOfWeek = (startDateStr) => {
        if (!startDateStr) return [];

        const start = DateTime.fromISO(startDateStr).startOf("week"); // Monday
        const days = [];

        for (let i = 0; i < 7; i++) {
            const current = start.plus({ days: i });

            days.push({
                name: current.toFormat(DATE_FORMAT_SERVER),
                SPACEPARK_CURRENT_YEAR: 0,
                SPACEPARK_LAST_YEAR: 0,
                MELBOURNE_CURRENT_YEAR: 0,
                MELBOURNE_LAST_YEAR: 0,
                LOS_ANGELES_CURRENT_YEAR: 0,
                LOS_ANGELES_LAST_YEAR: 0
            });
        }

        return days;
    };

    // ============================================================
    // MULTI RANGE MODE — aggregated bar chart
    // ============================================================

    // Helper: map a raw API location name to a CITY_KEY_MAP entry
    // Unrecognized locations (e.g. "Italy", "Turin") are merged into SpacePark
    const mapLocationToCity = (locationName) => {
        if (CITY_KEY_MAP[locationName]) return locationName;
        // Fallback: merge unknown locations into SpacePark (Italy)
        return Object.keys(CITY_KEY_MAP)[0]; // "San Mauro Torinese (TO) - SpacePark"
    };

    const buildMultiRangeChartData = () => {
        if (!rawApiData?.current_data?.length) {
            setGraphData({ series: [], categories: [] });
            return;
        }

        const currentRanges = rawApiData.current_data || [];
        const previousRanges = rawApiData.previous_data || [];

        // Check if any last year series is visible
        const hasLastYearVisible = Object.values(CITY_KEY_MAP).some(cityKeys => 
            activeSeriesKeys.includes(cityKeys.last)
        );

        // Build category labels from ranges
        const categories = currentRanges.map(rangeItem => {
            const start = DateTime.fromISO(rangeItem.range.start);
            const end = DateTime.fromISO(rangeItem.range.end);

            let categoryLabel;
            if (appliedFilters.dateType === DATE_MONTHLY.value) {
                categoryLabel = start.toFormat("MMM yyyy");
            } else if (appliedFilters.dateType === DATE_YEARLY.value) {
                categoryLabel = start.toFormat("yyyy");
            } else if (appliedFilters.dateType === DATE_WEEKLY.value) {
                categoryLabel = `W${start.weekNumber} (${start.toFormat("dd/MM")} - ${end.toFormat("dd/MM")}) ${start.toFormat("yyyy")}`;
            } else {
                categoryLabel = `${start.toFormat("dd/MM")} - ${end.toFormat("dd/MM")}`;
            }

            // If last year is visible, append the corresponding last year period
            if (hasLastYearVisible) {
                const lastYearStart = start.minus({ years: 1 });
                const lastYearEnd = end.minus({ years: 1 });
                
                if (appliedFilters.dateType === DATE_MONTHLY.value) {
                    categoryLabel = `${categoryLabel} – ${lastYearStart.toFormat("MMM yyyy")}`;
                } else if (appliedFilters.dateType === DATE_YEARLY.value) {
                    categoryLabel = `${categoryLabel} – ${lastYearStart.toFormat("yyyy")}`;
                } else if (appliedFilters.dateType === DATE_WEEKLY.value) {
                    categoryLabel = `${categoryLabel} – W${lastYearStart.weekNumber} (${lastYearStart.toFormat("dd/MM")} - ${lastYearEnd.toFormat("dd/MM")}) ${lastYearStart.toFormat("yyyy")}`;
                } else {
                    categoryLabel = `${categoryLabel} – ${lastYearStart.toFormat("dd/MM")} - ${lastYearEnd.toFormat("dd/MM")}`;
                }
            }

            return categoryLabel;
        });

        // If a location filter is active, only process that city
        const selectedLocation = appliedFilters?.location?.value;
        const citiesToProcess = selectedLocation && CITY_KEY_MAP[selectedLocation]
            ? { [selectedLocation]: CITY_KEY_MAP[selectedLocation] }
            : CITY_KEY_MAP;

        // Build series using CITY_KEY_MAP keys (same as single-range chart)
        const series = [];

        Object.entries(citiesToProcess).forEach(([cityName, keys]) => {
            // Current year: aggregate all API locations that map to this city
            const currentData = currentRanges.map(rangeItem => {
                return (rangeItem.data || []).reduce((sum, loc) => {
                    if (mapLocationToCity(loc.location) === cityName) {
                        return sum + (loc.trend || []).reduce((s, t) => s + (Number(t.count) || 0), 0);
                    }
                    return sum;
                }, 0);
            });

            series.push({
                name: unslugify(keys.current), // e.g. "SPACEPARK CURRENT YEAR"
                data: currentData,
            });

            // Previous year: aggregate same way
            const prevData = previousRanges.map(rangeItem => {
                return (rangeItem.data || []).reduce((sum, loc) => {
                    if (mapLocationToCity(loc.location) === cityName) {
                        return sum + (loc.trend || []).reduce((s, t) => s + (Number(t.count) || 0), 0);
                    }
                    return sum;
                }, 0);
            });

            // Only add previous year series if there's any data
            if (prevData.some(v => v > 0)) {
                series.push({
                    name: unslugify(keys.last), // e.g. "SPACEPARK LAST YEAR"
                    data: prevData,
                });
            }
        });

        setGraphData({ series, categories });
    };

    // ============================================================
    // EFFECTS
    // ============================================================

    // Single-range: generate chart dates and merge with API data
    useEffect(() => {
        if (isMultiRange) return; // skip for multi-range

        const dateRanges = appliedFilters?.dateRanges;
        if (!dateRanges?.length || !appliedFilters?.dateType) return;

        let newDates = [];

        // Single range: use first range
        const range = dateRanges[0];
        switch (appliedFilters.dateType) {
            case DATE_YEARLY.value:
                newDates = getAllMonthsOfYear(range.start);
                break;
            case DATE_MONTHLY.value:
                newDates = getAllDatesOfMonth(range.start);
                break;
            case DATE_WEEKLY.value:
                newDates = getAllDaysOfWeek(range.start);
                break;
            case DATE_DAILY.value:
                break;
            default:
                break;
        }

        console.log("New Dates before merging:", newDates);
        const finalChartData = mergeCityYearData(newDates, data);
        setChartDates(finalChartData);
    }, [appliedFilters, data, isMultiRange]);

    // Multi-range: build aggregated bar chart data
    useEffect(() => {
        if (!isMultiRange) return;
        buildMultiRangeChartData();
    }, [appliedFilters, rawApiData, isMultiRange, activeSeriesKeys]);

    // Single-range: build series/categories from chartDates
    useEffect(() => {
        if (isMultiRange) return;
        if (!chartDates?.length) return;

        const keys = Object.keys(chartDates[0]).filter(k => k !== "name");

        const filteredKeys = keys.filter(k => activeSeriesKeys.includes(k));

        const series = filteredKeys.map(key => ({
            name: unslugify(key),
            data: chartDates.map(d => d[key] ?? 0),
        }));

        let categories;
        switch (appliedFilters?.dateType) {
            case DATE_YEARLY.value:
                categories = chartDates.map(d =>
                    DateTime.fromISO(d.name).toFormat("MMM")
                );
                break;
            case DATE_MONTHLY.value:
                categories = chartDates.map(d =>
                    DateTime.fromISO(d.name).toFormat("dd")
                );
                break;
            case DATE_WEEKLY.value:
                categories = chartDates.map(d =>
                    DateTime.fromISO(d.name).toFormat("LLL dd")
                );
                break;
            default:
                categories = [];
        }

        setGraphData({ series, categories });
    }, [appliedFilters, chartDates, activeSeriesKeys, isMultiRange]);

    // Initialize active series keys based on location filter
    // Runs immediately when appliedFilters changes, before series-building effect
    useEffect(() => {
        const selectedLocation = appliedFilters?.location?.value;
        console.log("Location filter value:", selectedLocation, "CITY_KEY_MAP keys:", Object.keys(CITY_KEY_MAP), "Match:", CITY_KEY_MAP[selectedLocation]);

        if (selectedLocation && CITY_KEY_MAP[selectedLocation]) {
            // Location selected: show only that location's current + last year keys
            const locationKeys = CITY_KEY_MAP[selectedLocation];
            setActiveSeriesKeys([locationKeys.current, locationKeys.last]);
        } else {
            // No location filter: show all current year keys (exclude last year)
            const currentKeys = Object.values(CITY_KEY_MAP).map(city => city.current);
            setActiveSeriesKeys(currentKeys);
        }
    }, [appliedFilters]);

    const toggleSeriesKey = (key) => {
        setActiveSeriesKeys(prev =>
            prev.includes(key)
                ? prev.filter(k => k !== key)
                : [...prev, key]
        );
    };

    // Determine chart type:
    // - Multi-range: always bar chart
    // - Single range + yearly: bar chart
    // - Single range + weekly/monthly: line chart
    const useBarChart = isMultiRange || appliedFilters?.dateType === DATE_YEARLY.value;

    // Build dynamic x-axis title based on dateType and number of ranges
    const rangeCount = appliedFilters?.dateRanges?.length || 1;
    const xAxisTitle = (() => {
        switch (appliedFilters?.dateType) {
            case DATE_MONTHLY.value:
                return rangeCount > 1 ? `Duration (${rangeCount} Months)` : 'Duration (Month)';
            case DATE_WEEKLY.value:
                return rangeCount > 1 ? `Duration (${rangeCount} Weeks)` : 'Duration (Week)';
            case DATE_YEARLY.value:
                return rangeCount > 1 ? `Duration (${rangeCount} Years)` : 'Duration (Year)';
            default:
                return 'Duration';
        }
    })();

    return (

        <div className='mt-11'>
            {
                useBarChart ?
                    <MultiLineBarChart graphData={graphData} title={''} activeSeriesKeys={activeSeriesKeys} toggleSeriesKey={toggleSeriesKey} selectedLocation={appliedFilters?.location?.value} xAxisTitle={xAxisTitle} />
                    :
                    <MultiLineChart graphData={graphData} title={''} activeSeriesKeys={activeSeriesKeys} toggleSeriesKey={toggleSeriesKey} selectedLocation={appliedFilters?.location?.value} xAxisTitle={xAxisTitle} />
            }
        </div>
    )
}

export default ChartContainer